import { useLiveQuery } from '@electric-sql/pglite-react';
import {
  formatRupees,
  PAYMENT_METHOD_LABELS,
  type PaymentMethod,
  type ReportStatus,
} from '@quickreport/domain';
import {
  Badge,
  BarList,
  ChartCard,
  ColumnChart,
  compactRupees,
  dayLabel,
  Input,
  LineChart,
  Meter,
  Sparkline,
  useDismiss,
  WideSparkline,
} from '@quickreport/ui';
import { type DashboardData, loadDashboard } from '@quickreport/workspace';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  ArrowDownRight,
  ArrowUpRight,
  Ban,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleDashed,
  ClipboardCheck,
  FilePen,
  PenLine,
  RotateCcw,
} from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkspace } from '../../lib/workspace';
import { STATUS_LABEL } from '../reports/context';
import { PAY_TONE } from '../reports/PaymentPanel';
import {
  bucket,
  changePct,
  durationText,
  PRESETS,
  type Preset,
  previousRange,
  type Range,
  rangeFor,
  rangeText,
} from './range';

const STATUS_ORDER: ReportStatus[] = [
  'draft',
  'awaiting_verification',
  'awaiting_signature',
  'partial',
  'final',
  'amended',
  'revoked',
];
const STATUS_ICON: Record<ReportStatus, ReactNode> = {
  draft: <FilePen size={14} />,
  awaiting_verification: <ClipboardCheck size={14} />,
  awaiting_signature: <PenLine size={14} />,
  partial: <CircleDashed size={14} />,
  final: <CheckCircle2 size={14} />,
  amended: <RotateCcw size={14} />,
  revoked: <Ban size={14} />,
};

const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
const saved = <T,>(key: string, fallback: T): T => {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
};
const keep = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // remembering the choice is a convenience only
  }
};

export function DashboardPage() {
  const ws = useWorkspace();
  const finance = ws.can('view_finance');
  const [preset, setPreset] = useState<Preset>(() => saved<Preset>('qr.dash.preset', '30'));
  const [custom, setCustom] = useState<Range>(() =>
    saved<Range>('qr.dash.custom', rangeFor('30', { from: '', to: '' })),
  );
  const range = useMemo(() => rangeFor(preset, custom), [preset, custom]);
  const before = useMemo(() => previousRange(range), [range]);

  const [data, setData] = useState<DashboardData | null>(null);
  const [prev, setPrev] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reload when this computer's data changes, whether from typing here or from a sync.
  const stamp = useLiveQuery<{ k: string }>(
    `select concat_ws('|',
        (select max(updated_at) from reports where org_id = $1),
        (select count(*) from reports where org_id = $1),
        (select max(updated_at) from payments where org_id = $1),
        (select count(*) from report_versions where org_id = $1)) as k`,
    [ws.orgId],
  );
  const key = stamp?.rows[0]?.k ?? '';

  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` is the signal that the data changed
  useEffect(() => {
    if (stamp === undefined) return;
    let alive = true;
    setLoading(true);
    const opts = { orgId: ws.orgId, timeZone: tz(), finance };
    (async () => {
      try {
        const cur = await loadDashboard(ws.db, { ...opts, ...range });
        const old = await loadDashboard(ws.db, { ...opts, ...before });
        if (!alive) return;
        setData(cur);
        setPrev(old);
        setError(null);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'Could not load the dashboard');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [ws.db, ws.orgId, finance, range.from, range.to, before.from, before.to, key]);

  return (
    <DashboardView
      range={range}
      preset={preset}
      custom={custom}
      data={data}
      prev={prev}
      loading={loading}
      error={error}
      finance={finance}
      onPreset={(p) => {
        setPreset(p);
        keep('qr.dash.preset', p);
      }}
      onCustom={(r) => {
        setCustom(r);
        setPreset('custom');
        keep('qr.dash.custom', r);
        keep('qr.dash.preset', 'custom');
      }}
    />
  );
}

/** The dashboard as a picture of the data it is given. */
export function DashboardView({
  range,
  preset,
  custom,
  data,
  prev,
  loading,
  error,
  finance,
  onPreset,
  onCustom,
}: {
  range: Range;
  preset: Preset;
  custom: Range;
  data: DashboardData | null;
  prev: DashboardData | null;
  loading: boolean;
  error: string | null;
  finance: boolean;
  onPreset: (p: Exclude<Preset, 'custom'>) => void;
  onCustom: (r: Range) => void;
}) {
  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Dashboard</h1>
          <p className="mt-1 text-slate">
            How the lab is doing, {rangeText(range)}. Works offline, from this computer’s data.
          </p>
        </div>
        <RangePicker
          preset={preset}
          custom={custom}
          range={range}
          onPreset={onPreset}
          onCustom={onCustom}
        />
      </div>

      {error ? (
        <p role="alert" className="mt-6 rounded-md bg-red-50 px-3 py-2 text-flag">
          {error}
        </p>
      ) : null}

      {data ? (
        <div
          className={clsx(
            'mt-6 grid grid-cols-1 gap-4 transition-opacity',
            loading && 'opacity-60',
          )}
          aria-busy={loading}
        >
          <Headline data={data} prev={prev} finance={finance} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[3fr_2fr]">
            <TimeCard data={data} />
            <StatusCard data={data} />
          </div>
          {data.finance ? <MoneyRow data={data} fin={data.finance} /> : null}
          {data.finance ? <SpendRow data={data} fin={data.finance} /> : null}
          <div
            className={clsx(
              'grid grid-cols-1 gap-4',
              data.finance ? 'xl:grid-cols-3' : 'xl:grid-cols-2',
            )}
          >
            <TestsCard data={data} />
            <SamplesCard data={data} />
            {data.finance ? <OwingCard fin={data.finance} /> : null}
          </div>
        </div>
      ) : error ? null : (
        <p className="mt-10 text-center text-slate">Loading…</p>
      )}
    </div>
  );
}

// ---- filter ---------------------------------------------------------------------------

function RangePicker({
  preset,
  custom,
  range,
  onPreset,
  onCustom,
}: {
  preset: Preset;
  custom: Range;
  range: Range;
  onPreset: (p: Exclude<Preset, 'custom'>) => void;
  onCustom: (r: Range) => void;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const label =
    preset === 'custom' ? rangeText(range) : PRESETS.find((p) => p.value === preset)?.label;
  const [from, setFrom] = useState(custom.from || range.from);
  const [to, setTo] = useState(custom.to || range.to);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-navy/25 bg-paper px-3 font-medium text-navy hover:bg-brand-soft"
      >
        {label}
        <ChevronDown size={15} />
      </button>
      {open ? (
        <div className="absolute right-0 z-30 mt-1 w-64 rounded-xl border border-line bg-paper py-1 shadow-lg">
          <div role="listbox" aria-label="Period">
            {PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                role="option"
                aria-selected={preset === p.value}
                onClick={() => {
                  onPreset(p.value);
                  setOpen(false);
                }}
                className="flex h-9 w-full items-center justify-between px-3 text-left hover:bg-canvas"
              >
                {p.label}
                {preset === p.value ? <Check size={16} strokeWidth={3} /> : null}
              </button>
            ))}
          </div>
          <div className="mt-1 grid gap-2 border-t border-line p-3">
            <span className="text-[12px] font-medium text-slate">Custom range</span>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                aria-label="From date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
              />
              <Input
                type="date"
                aria-label="To date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <button
              type="button"
              disabled={!from || !to}
              onClick={() => {
                onCustom({ from, to });
                setOpen(false);
              }}
              className="h-8 rounded-lg bg-navy px-3 font-medium text-white hover:bg-navy-hover disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- headline numbers ----------------------------------------------------------------

function Delta({
  now,
  before,
  upIsGood = true,
  versus,
}: {
  now: number;
  before: number;
  upIsGood?: boolean;
  versus: string;
}) {
  const pct = changePct(now, before);
  if (pct === null)
    return <span className="text-[12px] text-slate">No earlier period to compare</span>;
  const up = pct >= 0;
  const good = pct === 0 ? null : up === upIsGood;
  const Arrow = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-1 text-[12px]">
      <span
        className="inline-flex items-center gap-0.5 font-medium"
        style={{
          color:
            good === null
              ? 'var(--viz-muted)'
              : good
                ? 'var(--viz-good-text)'
                : 'var(--viz-bad-text)',
        }}
      >
        <Arrow size={14} aria-hidden /> {up ? '+' : '−'}
        {Math.abs(pct)}%
      </span>
      <span className="text-slate">vs {versus}</span>
    </span>
  );
}

function Tile({
  label,
  value,
  note,
  trend,
  trendLabel,
}: {
  label: string;
  value: string;
  note?: ReactNode;
  trend?: number[];
  trendLabel?: string;
}) {
  return (
    <div className="viz-root flex min-w-0 flex-col rounded-xl border border-line bg-[var(--surface-1)] p-4">
      <div className="text-[12px] text-slate">{label}</div>
      <div className="mt-1 text-[24px] leading-tight font-semibold text-ink">{value}</div>
      {note ? <div className="mt-1.5">{note}</div> : null}
      {trend && trend.length > 1 ? (
        <div className="mt-auto pt-3">
          <Sparkline values={trend} label={trendLabel ?? `${label} trend`} />
        </div>
      ) : null}
    </div>
  );
}

function Headline({
  data,
  prev,
  finance,
}: {
  data: DashboardData;
  prev: DashboardData | null;
  finance: boolean;
}) {
  const f = data.finance;
  const days = data.days.length;
  const versus = days === 1 ? 'the day before' : `the previous ${days} days`;
  const created = data.days.map((d) => d.created);
  const signed = data.days.map((d) => d.signed);
  const received = data.days.map((d) => d.receivedPaise);
  const collected = f && f.billedPaise > 0 ? f.receivedPaise / f.billedPaise : null;
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_3fr]">
      <section className="viz-root flex min-w-0 flex-col justify-between rounded-xl border border-line bg-[var(--surface-1)] p-5">
        <div>
          <div className="text-[13px] text-slate">
            {finance ? 'Money received' : 'Reports made'}
          </div>
          <div className="mt-1 text-[56px] leading-none font-semibold tracking-tight text-ink">
            {finance && f
              ? formatRupees(f.receivedPaise)
              : data.kpi.created.toLocaleString('en-IN')}
          </div>
          <div className="mt-3">
            {prev ? (
              <Delta
                now={finance && f ? f.receivedPaise : data.kpi.created}
                before={finance && prev.finance ? prev.finance.receivedPaise : prev.kpi.created}
                versus={versus}
              />
            ) : null}
          </div>
        </div>
        <div className="mt-4">
          <WideSparkline
            values={bucket(finance && f ? received : created, 30)}
            label={finance && f ? 'Money received per period' : 'Reports made per period'}
          />
        </div>
        {finance && f ? (
          <div className="mt-4">
            <div className="mb-1.5 flex items-baseline justify-between text-[12px] text-slate">
              <span>Collected of {formatRupees(f.billedPaise)} billed</span>
              <span className="font-medium text-ink">
                {collected === null ? '—' : `${Math.round(collected * 100)}%`}
              </span>
            </div>
            <Meter fraction={collected ?? 0} label="Share of the amount billed that was received" />
          </div>
        ) : null}
      </section>

      <div className={clsx('grid grid-cols-1 gap-4 sm:grid-cols-2', f && 'xl:grid-cols-4')}>
        {finance ? (
          <Tile
            label="Reports made"
            value={data.kpi.created.toLocaleString('en-IN')}
            note={
              prev ? (
                <Delta now={data.kpi.created} before={prev.kpi.created} versus={versus} />
              ) : null
            }
            trend={bucket(created)}
            trendLabel="Reports made per period"
          />
        ) : null}
        <Tile
          label="Reports signed"
          value={data.kpi.signed.toLocaleString('en-IN')}
          note={
            prev ? <Delta now={data.kpi.signed} before={prev.kpi.signed} versus={versus} /> : null
          }
          trend={bucket(signed)}
          trendLabel="Reports signed per period"
        />
        <Tile
          label="Waiting for a signature or checks"
          value={data.kpi.waiting.toLocaleString('en-IN')}
          note={
            data.kpi.dueToday + data.kpi.overdue > 0 ? (
              <Link to="/notifications" className="text-[12px] font-medium text-flag underline">
                {data.kpi.dueToday} due today · {data.kpi.overdue} overdue
              </Link>
            ) : (
              <span className="text-[12px] text-slate">None are due today or late</span>
            )
          }
        />
        <Tile
          label="Time from collection to signing"
          value={durationText(data.kpi.medianTurnaroundHours)}
          note={<span className="text-[12px] text-slate">Typical (median) for reports signed</span>}
        />
        {f ? (
          <>
            <Tile
              label="Billed"
              value={formatRupees(f.billedPaise)}
              note={<span className="text-[12px] text-slate">Reports made in this period</span>}
              trend={bucket(received)}
              trendLabel="Money received per period"
            />
            <Tile
              label="Still to collect"
              value={formatRupees(f.outstandingPaise)}
              note={
                <span className="text-[12px] text-slate">
                  Across all reports, not only this period
                </span>
              }
            />
            <Tile
              label="Spent"
              value={formatRupees(f.expensesPaise)}
              note={
                prev?.finance ? (
                  <Delta
                    now={f.expensesPaise}
                    before={prev.finance.expensesPaise}
                    upIsGood={false}
                    versus={versus}
                  />
                ) : (
                  <span className="text-[12px] text-slate">Expenses in this period</span>
                )
              }
              trend={bucket(data.days.map((d) => d.spentPaise))}
              trendLabel="Money spent per period"
            />
            <Tile
              label="Received less spent"
              value={formatRupees(f.receivedPaise - f.expensesPaise)}
              note={
                <span className="text-[12px] text-slate">
                  {formatRupees(f.receivedPaise)} in, {formatRupees(f.expensesPaise)} out
                </span>
              }
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

// ---- charts --------------------------------------------------------------------------

function TimeCard({ data }: { data: DashboardData }) {
  const days = data.days.map((d) => d.day);
  const empty = data.kpi.created === 0 && data.kpi.signed === 0;
  const series = [
    {
      key: 'created',
      label: 'Made',
      color: 'var(--series-1)',
      values: data.days.map((d) => d.created),
    },
    {
      key: 'signed',
      label: 'Signed',
      color: 'var(--series-2)',
      values: data.days.map((d) => d.signed),
    },
  ];
  return (
    <ChartCard
      title="Reports over time"
      subtitle="Reports made and reports signed each day"
      legend={series.map((s) => ({ label: s.label, color: s.color }))}
      table={{
        head: ['Day', 'Made', 'Signed'],
        rows: data.days.map((d) => [dayLabel(d.day, true), d.created, d.signed]),
      }}
    >
      {empty ? (
        <Empty>No reports were made or signed in this period.</Empty>
      ) : (
        <LineChart
          days={days}
          series={series}
          label="Reports made and signed each day"
          format={(n) => n.toLocaleString('en-IN')}
        />
      )}
    </ChartCard>
  );
}

function StatusCard({ data }: { data: DashboardData }) {
  const by = new Map(data.statuses.map((s) => [s.status, s.n]));
  const rows = STATUS_ORDER.filter((s) => by.has(s)).map((s) => ({
    key: s,
    label: STATUS_LABEL[s] ?? s,
    value: by.get(s) ?? 0,
    icon: STATUS_ICON[s],
    // A revoked report is a bad outcome, so it alone wears the reserved warning colour.
    ...(s === 'revoked' ? { color: '#d03b3b' } : {}),
  }));
  return (
    <ChartCard
      title="Where the reports are"
      subtitle="Reports made in this period, by stage"
      table={{ head: ['Stage', 'Reports'], rows: rows.map((r) => [r.label, r.value]) }}
    >
      {rows.length === 0 ? (
        <Empty>No reports were made in this period.</Empty>
      ) : (
        <BarList rows={rows} unit="reports" label="Reports by stage" />
      )}
    </ChartCard>
  );
}

function MoneyRow({
  data,
  fin,
}: {
  data: DashboardData;
  fin: NonNullable<DashboardData['finance']>;
}) {
  const days = data.days.map((d) => d.day);
  const values = data.days.map((d) => d.receivedPaise);
  const rows = fin.byMethod.map((m) => ({
    key: m.method,
    label: PAYMENT_METHOD_LABELS[m.method as PaymentMethod] ?? m.method,
    value: m.paise,
  }));
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[3fr_2fr]">
      <ChartCard
        title="Money received each day"
        subtitle="Payments recorded, by the day they were received"
        table={{
          head: ['Day', 'Received'],
          rows: data.days.map((d) => [dayLabel(d.day, true), formatRupees(d.receivedPaise)]),
        }}
      >
        {fin.receivedPaise === 0 ? (
          <Empty>No payments were recorded in this period.</Empty>
        ) : (
          <ColumnChart
            days={days}
            values={values}
            color="var(--series-1)"
            name="received"
            format={compactRupees}
            label="Money received each day"
          />
        )}
      </ChartCard>
      <ChartCard
        title="How patients paid"
        subtitle="Total received, by way of payment"
        table={{
          head: ['Paid by', 'Received'],
          rows: rows.map((r) => [r.label, formatRupees(r.value)]),
        }}
      >
        {rows.length === 0 ? (
          <Empty>No payments were recorded in this period.</Empty>
        ) : (
          <BarList
            rows={rows}
            unit="received"
            format={compactRupees}
            label="Money received by way of payment"
          />
        )}
      </ChartCard>
    </div>
  );
}

function TestsCard({ data }: { data: DashboardData }) {
  const rows = data.topTests.map((t) => ({ key: t.name, label: t.name, value: t.n }));
  return (
    <ChartCard
      title="Most ordered tests"
      subtitle="Reports made in this period"
      table={{ head: ['Test', 'Reports'], rows: rows.map((r) => [r.label, r.value]) }}
    >
      {rows.length === 0 ? (
        <Empty>No tests were ordered in this period.</Empty>
      ) : (
        <BarList rows={rows} unit="reports" label="Most ordered tests" />
      )}
    </ChartCard>
  );
}

function SamplesCard({ data }: { data: DashboardData }) {
  const rows = data.samples.map((s) => ({ key: s.type, label: s.type, value: s.n }));
  return (
    <ChartCard
      title="Samples collected"
      subtitle="By type, for reports made in this period"
      table={{ head: ['Sample', 'Collected'], rows: rows.map((r) => [r.label, r.value]) }}
    >
      {rows.length === 0 ? (
        <Empty>No samples were recorded in this period.</Empty>
      ) : (
        <BarList rows={rows} unit="samples" label="Samples collected by type" />
      )}
    </ChartCard>
  );
}

function OwingCard({ fin }: { fin: NonNullable<DashboardData['finance']> }) {
  return (
    <section className="viz-root min-w-0 rounded-xl border border-line bg-[var(--surface-1)] p-4">
      <h2 className="text-[14px] font-semibold text-ink">Biggest balances to collect</h2>
      <p className="text-[12px] text-slate">Reports with money still to pay, any date</p>
      {fin.owing.length === 0 ? (
        <div className="mt-3">
          <Empty>Nothing is owed. Every report is paid.</Empty>
        </div>
      ) : (
        <ul className="mt-3 grid gap-1">
          {fin.owing.map((o) => (
            <li key={o.id}>
              <Link
                to="/reports/$reportId"
                params={{ reportId: o.id }}
                className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-canvas"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-ink">
                    {o.patient || o.name}
                  </span>
                  <span className="block truncate text-[12px] text-slate">
                    {o.labNo} · paid {formatRupees(o.paidPaise)} of {formatRupees(o.duePaise)}
                  </span>
                </span>
                <Badge tone={PAY_TONE[o.paidPaise > 0 ? 'part' : 'unpaid']}>
                  {formatRupees(o.duePaise - o.paidPaise)}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="py-10 text-center text-[13px] text-slate">{children}</p>;
}

/** Money in against money out each day, and where the money went. */
function SpendRow({
  data,
  fin,
}: {
  data: DashboardData;
  fin: NonNullable<DashboardData['finance']>;
}) {
  const days = data.days.map((d) => d.day);
  const rows = fin.expensesByCategory.map((c) => ({
    key: c.category,
    label: c.category,
    value: c.paise,
  }));
  const empty = fin.receivedPaise === 0 && fin.expensesPaise === 0;
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[3fr_2fr]">
      <ChartCard
        title="Money in and money out"
        subtitle="Received from patients, and spent on the lab, each day"
        legend={[
          { label: 'Received', color: 'var(--series-1)' },
          { label: 'Spent', color: 'var(--series-2)' },
        ]}
        table={{
          head: ['Day', 'Received', 'Spent'],
          rows: data.days.map((d) => [
            dayLabel(d.day, true),
            formatRupees(d.receivedPaise),
            formatRupees(d.spentPaise),
          ]),
        }}
      >
        {empty ? (
          <Empty>No money came in or went out in this period.</Empty>
        ) : (
          <LineChart
            days={days}
            series={[
              {
                key: 'in',
                label: 'Received',
                color: 'var(--series-1)',
                values: data.days.map((d) => d.receivedPaise),
              },
              {
                key: 'out',
                label: 'Spent',
                color: 'var(--series-2)',
                values: data.days.map((d) => d.spentPaise),
              },
            ]}
            format={compactRupees}
            label="Money received and spent each day"
          />
        )}
      </ChartCard>
      <ChartCard
        title="Where the money went"
        subtitle="Expenses in this period, by kind"
        table={{ head: ['Kind', 'Spent'], rows: rows.map((r) => [r.label, formatRupees(r.value)]) }}
      >
        {rows.length === 0 ? (
          <Empty>No expenses were recorded in this period.</Empty>
        ) : (
          <BarList
            rows={rows}
            unit="spent"
            color="var(--series-2)"
            format={compactRupees}
            label="Expenses by kind"
          />
        )}
      </ChartCard>
    </div>
  );
}
